import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../../../components/ui";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import type { MapFilterGroup } from "../components/MapFilterMenu";
import RefreshSectionButton from "../components/RefreshSectionButton";
import MonitoringFeed from "../components/MonitoringFeed";
import MonitoringRoster from "../components/MonitoringRoster";
import { useResumenRastro } from "../components/RastroDelDia";
import LimiteDeError from "../../../components/ui/LimiteDeError";
import MonitoringCasePanel from "../components/MonitoringCasePanel";
import MonitoringUnitPanel from "../components/MonitoringUnitPanel";
import TabLoader from "../components/TabLoader";
import TacticalMap from "../components/TacticalMap";
import { useMonitoringCenter } from "../hooks/useMonitoringCenter";
import { WATCH_STATUS_MAP_COLORS } from "../theme";
import { WATCH_STATUSES, type WatchStatus } from "../types";
import type { CentinelaCaseView } from "../services/mapCentinelaCase";

/**
 * Centro de monitoreo.
 *
 * ── Por qué ya no hay encabezado ────────────────────────────────────────────
 * Tenía título, subtítulo con el conteo de unidades, tres chips de conteo y una
 * fila de filtros de amenaza que crecía con el catálogo. Entre todo se comían
 * unos 120 px de alto, y el contenido quedaba con `overflow-y-auto`: había que
 * hacer scroll para ver el mapa completo.
 *
 * Un centro de monitoreo que no cabe en la pantalla no es un centro de
 * monitoreo. Lo que decía el encabezado ya está en otro lado —el conteo de
 * unidades, casos y geocercas vive en la leyenda del propio mapa— así que
 * quitarlo no perdió información, solo recuperó el alto.
 *
 * Queda una barra mínima con la píldora de vigilancia y el botón de recargar,
 * que son acciones y no rótulos.
 */
export default function CentroMonitoreoSection() {
  const { t } = useTranslation("common");
  const {
    visibleRows,
    casesLoading,
    selectedRow,
    selectedUnitId,
    selectedCase,
    selectedCaseId,
    selectUnit,
    selectCase,
    clearCase,
    liveOf,
    threats,
    labelOfCase,
    threatId,
    selectThreat,
    refresh,
    cases,
    openCases,
    geofences,
    corridors,
    totalUnits,
    isLoading,
    isRefreshing,
    casesUnavailable,
    isError,
    error,
  } = useMonitoringCenter();

  // Filtro por estado de vigilancia. Vive aquí y no en el hook porque es una
  // preferencia de esta vista: el roster y el mapa deben mostrar lo mismo, pero
  // ninguna otra sección lo consulta.
  const [hiddenStatuses, setHiddenStatuses] = useState<ReadonlySet<WatchStatus>>(
    () => new Set<WatchStatus>(),
  );

  const statusCounts = useMemo(() => {
    const counts = new Map<WatchStatus, number>();
    for (const status of WATCH_STATUSES) counts.set(status, 0);
    for (const row of visibleRows) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
    return counts;
  }, [visibleRows]);

  const rows = useMemo(
    () => (hiddenStatuses.size === 0 ? visibleRows : visibleRows.filter((row) => !hiddenStatuses.has(row.status))),
    [visibleRows, hiddenStatuses],
  );

  const toggleStatus = useCallback((id: string) => {
    setHiddenStatuses((current) => {
      const next = new Set(current);
      const status = id as WatchStatus;
      // El conjunto guarda lo OCULTO, así que «activo» en el menú es «no está
      // en el conjunto». Guardar lo visible obligaría a inicializarlo con los
      // seis estados y a mantenerlo si mañana se agrega uno nuevo.
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const filterGroups = useMemo<MapFilterGroup[]>(() => {
    const groups: MapFilterGroup[] = [
      {
        id: "status",
        label: t("monitoreoIa.monitoringCenter.filters.statusGroup"),
        mode: "multi",
        options: WATCH_STATUSES.map((status) => ({
          id: status,
          label: t(`monitoreoIa.status.${status}`),
          count: statusCounts.get(status) ?? 0,
          active: !hiddenStatuses.has(status),
          tone: WATCH_STATUS_MAP_COLORS[status],
        })),
        onToggle: toggleStatus,
      },
    ];

    if (threats.length > 0) {
      groups.push({
        id: "threats",
        label: t("monitoreoIa.monitoringCenter.threats"),
        mode: "single",
        options: threats.map((threat) => ({
          id: threat.id,
          label: threat.label,
          count: threat.open,
          active: threatId === threat.id,
        })),
        onToggle: (id) => selectThreat(threatId === id ? null : id),
      });
    }

    return groups;
  }, [t, threats, threatId, selectThreat, statusCounts, hiddenStatuses, toggleStatus]);

  const activeFilterCount = (threatId ? 1 : 0) + hiddenStatuses.size;

  const clearFilters = useCallback(() => {
    selectThreat(null);
    setHiddenStatuses(new Set<WatchStatus>());
  }, [selectThreat]);

  /*
    ── El modo análisis ──────────────────────────────────────────────────────
    Un solo interruptor abre dos cosas: la capa del rastro sobre el mapa y el
    panel de inteligencia en vivo a la derecha. Son las dos mitades de la misma
    intención, y en dos botones separados había que recordar cuál era cuál.

    Arranca APAGADO. Encendido por omisión, la inteligencia se comía un tercio
    de la pantalla desde el primer segundo y el mapa —que es a lo que se entra—
    quedaba de relleno. Además el rastro sin unidad elegida no dibuja nada, así
    que el panel abierto prometía un análisis que todavía no existía.

    Se enciende cuando alguien lo pide, y entonces aparecen las dos mitades: la
    capa sobre el mapa y la lista de casos al lado.

    ⚠️ Va ARRIBA de los `return` de `isLoading` e `isError`. Debajo, el hook no
    corre en el primer render y React tira «Rendered more hooks than during the
    previous render»: pantalla en negro.
  */
  const [modoAnalisis, setModoAnalisis] = useState(false);
  /** La unidad cuyo rastro se dibuja. Se elige desde la lista. */
  const [rastroDeviceId, setRastroDeviceId] = useState<string | null>(null);
  /** La ventana elegida en la fila de la unidad, en horas hacia atrás. */
  const [rastroHoras, setRastroHoras] = useState(6);
  /**
   * Si el caso que se va a abrir viene del atajo «Resolver».
   *
   * Se apaga en CUALQUIER otro camino de apertura. Sin eso, usar el atajo una
   * vez dejaba la bandera encendida y el siguiente caso —abierto desde la
   * lista, para mirarlo— también salía con el formulario de cierre desplegado.
   * Invitar a cerrar un caso que alguien sólo venía a leer es exactamente lo
   * que el flujo de dos pasos existe para evitar.
   */
  const [abrirEnCierre, setAbrirEnCierre] = useState(false);
  const [panelMovil, setPanelMovil] = useState<"lista" | "mapa" | "ficha">("mapa");

  /**
   * ── Llegar aquí con un caso ya abierto ────────────────────────────────
   *
   * Los atajos «Resolver» de otras pantallas —la tarjeta de la unidad en
   * Unidades, el saludo de Centinela en Resumen— navegan a
   * `?caso=<id>&accion=resolver`. Sin esto, el atajo sólo cambiaba de sección
   * y dejaba a la persona buscando el caso a mano en una lista, que es
   * exactamente lo que el atajo venía a evitar.
   *
   * Los parámetros se limpian en cuanto se usan: dejarlos puestos haría que
   * recargar la página, o volver con el botón de atrás, reabriera el
   * formulario de cierre de un caso que quizá ya se cerró.
   */
  const [params, setParams] = useSearchParams();
  const casoDeLaUrl = params.get("caso");
  const accionDeLaUrl = params.get("accion");

  const abrirCaso = useCallback(
    (item: CentinelaCaseView, intencion: "ver" | "resolver" = "ver") => {
      setAbrirEnCierre(intencion === "resolver");
      setPanelMovil("ficha");
      selectCase(item);
    },
    [selectCase],
  );
  // Sólo para saber si hay que girar el icono de la fila mientras se lee el día.
  const rastro = useResumenRastro(rastroDeviceId, rastroHoras);

  useEffect(() => {
    if (!casoDeLaUrl) return;
    const buscado = cases.find((c) => c.id === casoDeLaUrl);
    // Todavía no cargan los casos: se espera a la siguiente vuelta en vez de
    // limpiar la URL, o el atajo se perdería por llegar antes que los datos.
    if (!buscado) return;

    abrirCaso(buscado, accionDeLaUrl === "resolver" ? "resolver" : "ver");

    const limpios = new URLSearchParams(params);
    limpios.delete("caso");
    limpios.delete("accion");
    setParams(limpios, { replace: true });
  }, [casoDeLaUrl, accionDeLaUrl, cases, abrirCaso, params, setParams]);

  const hayTercero = Boolean(selectedCase || selectedRow || modoAnalisis);
  const panelActivo = panelMovil === "ficha" && !hayTercero ? "mapa" : panelMovil;
  const tabsMovil = [
    { id: "lista" as const, icon: "ri-list-check-2", label: t("monitoreoIa.monitoringCenter.tabs.list", { defaultValue: "Lista" }) },
    { id: "mapa" as const, icon: "ri-map-2-line", label: t("monitoreoIa.monitoringCenter.tabs.map", { defaultValue: "Mapa" }) },
    ...(hayTercero
      ? [
          {
            id: "ficha" as const,
            icon: selectedCase || selectedRow ? "ri-profile-line" : "ri-alarm-warning-line",
            label:
              selectedCase || selectedRow
                ? t("monitoreoIa.monitoringCenter.tabs.detail", { defaultValue: "Ficha" })
                : t("monitoreoIa.monitoringCenter.tabs.cases", { defaultValue: "Casos" }),
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <TabLoader label={t("monitoreoIa.monitoringCenter.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <EmptyState
          icon="ri-error-warning-line"
          title={t("monitoreoIa.errors.units", { defaultValue: "No se pudo cargar la flota." })}
          description={error ?? undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-3 py-1.5 max-lg:gap-x-3 max-lg:gap-y-2 max-lg:py-2.5">
        <CentinelaWatchPill />
        {openCases.length > 0 ? (
          <span className="inline-flex h-6 items-center gap-1.5 rounded-lg border border-error/30 bg-error/10 px-2 text-[10.5px] font-semibold text-error max-lg:h-10 max-lg:gap-2 max-lg:px-3.5 max-lg:text-xs">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-error" />
            </span>
            {t("monitoreoIa.monitoringCenter.openCases", { count: openCases.length })}
          </span>
        ) : null}
        <div className="ml-auto">
          <RefreshSectionButton onRefresh={refresh} loading={isRefreshing} />
        </div>
      </div>

      {/* Sin `overflow-y-auto`: cada columna hace su propio scroll y el bloque
          entero se queda dentro del alto disponible. Con scroll en el padre, el
          mapa se salía de la pantalla y había que bajar para verlo completo. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2 sm:p-3">
        <div role="tablist" className="grid shrink-0 grid-flow-col auto-cols-fr gap-1 rounded-xl border border-line bg-surface p-1 md:hidden">
          {tabsMovil.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={panelActivo === tab.id}
              onClick={() => setPanelMovil(tab.id)}
              className={`flex h-11 items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors ${
                panelActivo === tab.id
                  ? "bg-primary-500 text-on-primary"
                  : "text-content-secondary hover:bg-subtle"
              }`}
            >
              <i className={tab.icon} aria-hidden="true" />
              {tab.label}
            </button>
          ))}
        </div>
        {/*
          ── Por qué las columnas ya no son tres fijas ─────────────────────
          Antes había SIEMPRE una tercera columna de 340 px: con unidad
          seleccionada enseñaba su tarjeta, y sin ella la inteligencia en vivo.
          Eso tenía dos problemas. La tarjeta de la unidad —que ahora es una
          ficha forense con retratos, requisitos, medición y telemetría— vivía
          apretada en el ancho que se había elegido para una lista de casos. Y
          la inteligencia estaba abierta siempre, ocupando un tercio de la
          pantalla incluso cuando lo que se quería era mirar el mapa.

          Ahora la tercera columna aparece sólo si hay algo que poner, y su
          ancho depende de qué sea: 400 px para la ficha, 340 para la lista.
        */}
        <div
          className={`grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] gap-px overflow-hidden rounded-2xl border border-line bg-line md:grid-cols-[248px_1fr] ${
            hayTercero
              ? `md:grid-rows-[minmax(0,1fr)_minmax(0,18rem)] xl:grid-rows-1 ${
                  selectedRow ? "xl:grid-cols-[248px_1fr_400px]" : "xl:grid-cols-[248px_1fr_340px]"
                }`
              : "md:grid-rows-1 xl:grid-cols-[248px_1fr]"
          }`}
        >
          <div className={`min-h-0 flex-col ${panelActivo === "lista" ? "flex" : "hidden"} md:flex ${hayTercero ? "md:row-span-2 xl:row-span-1" : ""}`}>
            <LimiteDeError zona="la lista de unidades">
            <MonitoringRoster
              rows={rows}
              selectedUnitId={selectedUnitId}
              onSelect={(unitId) => {
                selectUnit(unitId);
                unitId && setPanelMovil("ficha");
              }}
              rastroDeviceId={rastroDeviceId}
              rastroCargando={rastro.isLoading}
              onVerRastro={(deviceId, horas) => {
                // Elegir la ventana ya es pedir el rastro: un segundo botón
                // para «aplicar» sobraría. Volver a elegir la misma unidad y la
                // misma ventana lo apaga.
                setRastroHoras(horas);
                setRastroDeviceId((actual) =>
                  actual === deviceId && rastroHoras === horas ? null : deviceId,
                );
              }}
            />
            </LimiteDeError>
          </div>

          <div className={`min-h-0 ${panelActivo === "mapa" ? "block" : "hidden"} md:block`}>
            <LimiteDeError zona="el mapa táctico">
            <TacticalMap
              rows={rows}
              totalUnits={totalUnits}
              geofences={geofences}
              corridors={corridors}
              openCases={openCases.length}
              selectedUnitId={selectedUnitId}
              onSelectUnit={selectUnit}
              filterGroups={filterGroups}
              activeFilterCount={activeFilterCount}
              onClearFilters={clearFilters}
              // `selectCase` espera el caso completo; el rastro sólo tiene su
              // id. Se resuelve contra la lista ya cargada en vez de pedirlo:
              // un caso del rastro siempre es uno del día que ya está en mano.
              verRastro={modoAnalisis}
              onToggleRastro={() => setModoAnalisis((v) => !v)}
              rastroDeviceId={rastroDeviceId}
              rastroHoras={rastroHoras}
              activeCaseId={selectedCaseId}
              onSelectCase={(id) => {
                const encontrado = cases.find((c) => c.id === id);
                if (encontrado) selectCase(encontrado);
              }}
            />
            </LimiteDeError>
          </div>

          {/* La ficha de la unidad MANDA sobre la inteligencia: si alguien
              seleccionó una unidad, es lo que está mirando. La lista de casos
              vuelve sola al cerrarla. */}
          {hayTercero ? (
          <div className={`min-h-0 flex-col ${panelActivo === "ficha" ? "flex" : "hidden"} md:flex`}>
            <LimiteDeError zona="el panel de la derecha">
            {/* Las tres se TURNAN en la misma columna, nunca se apilan. El caso
                gana sobre la unidad porque si alguien tocó un rombo del rastro
                o una fila de la inteligencia, eso es lo que quiere ver. */}
            {selectedCase ? (
              <MonitoringCasePanel
                key={selectedCase.id}
                caseView={selectedCase}
                row={selectedRow ?? null}
                labelOfCase={labelOfCase}
                onClose={clearCase}
                abrirEnCierre={abrirEnCierre}
              />
            ) : selectedRow ? (
              <MonitoringUnitPanel
                key={selectedRow.unitId}
                row={selectedRow}
                activeCase={selectedCase}
                labelOfCase={labelOfCase}
                casesLoading={casesLoading}
                onClose={() => selectUnit(null)}
                /* La intención viaja con el clic: «ver» abre el caso como
                   siempre, «resolver» lo abre con el formulario de cierre ya
                   desplegado. Sin esto el atajo sería el mismo enlace con otro
                   nombre, que es peor que no tenerlo. */
                onAbrirCaso={abrirCaso}
              />
            ) : (
              <MonitoringFeed
                cases={cases}
                labelOfCase={labelOfCase}
                liveOf={liveOf}
                activeCaseId={selectedCaseId}
                loading={casesLoading}
                unavailable={casesUnavailable}
                onSelectCase={abrirCaso}
                onOcultar={() => setModoAnalisis(false)}
              />
            )}
            </LimiteDeError>
          </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
